import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";

const RegistrarGrupoInstructores = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const isEdit = useMemo(() => Boolean(id), [id]);

  const [loading, setLoading] = useState(false);
  const [cargandoInicial, setCargandoInicial] = useState(false);

  const [grupos, setGrupos] = useState([]);
  const [instructores, setInstructores] = useState([]);

  const [grupoObjectId, setGrupoObjectId] = useState("");
  const [instructorObjectId, setInstructorObjectId] = useState("");

  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null);
  const [instructorSeleccionado, setInstructorSeleccionado] = useState(null);

  const [tipoAsignacion, setTipoAsignacion] = useState("PRINCIPAL");
  const [estadoAsignacion, setEstadoAsignacion] = useState("ACTIVO");
  const [observacionAsignacion, setObservacionAsignacion] = useState("");

  const authConfig = useCallback(() => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }, []);

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login");
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const selectStyles = useMemo(
    () => ({
      container: (base) => ({ ...base, width: "100%" }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: state.isFocused ? "#86b7fe" : base.borderColor,
        boxShadow: state.isFocused
          ? "0 0 0 .25rem rgba(13,110,253,.15)"
          : "none",
      }),
    }),
    []
  );

  const opcionesGrupos = useMemo(() => {
    return grupos.map((grupo) => ({
      value: grupo._id,
      label: `${grupo.fichaGrupo} - ${
        grupo.programaObjectId?.nombrePrograma || "Sin programa"
      }`,
    }));
  }, [grupos]);

  const opcionesInstructores = useMemo(() => {
    return instructores.map((instructor) => ({
      value: instructor._id,
      label: `${instructor.nombre1Instructor || ""} ${
        instructor.nombre2Instructor || ""
      } ${instructor.apellido1Instructor || ""} ${
        instructor.apellido2Instructor || ""
      } - ${instructor.documentoInstructor || "Sin documento"}`,
    }));
  }, [instructores]);

  const cleanData = useCallback(() => {
    setGrupoObjectId("");
    setInstructorObjectId("");
    setGrupoSeleccionado(null);
    setInstructorSeleccionado(null);
    setTipoAsignacion("PRINCIPAL");
    setEstadoAsignacion("ACTIVO");
    setObservacionAsignacion("");
    localStorage.removeItem("grupoInstructorEdit");
  }, []);

  const getGrupos = useCallback(async () => {
    const { data } = await axios.get("/api/grupos/listall", authConfig());
    setGrupos(Array.isArray(data?.grupos) ? data.grupos : []);
  }, [authConfig]);

  const getInstructores = useCallback(async () => {
    const { data } = await axios.get("/api/instructores/listall", authConfig());
    setInstructores(Array.isArray(data?.instructores) ? data.instructores : []);
  }, [authConfig]);

  const cargarDatosBase = useCallback(async () => {
    try {
      setCargandoInicial(true);
      await Promise.all([getGrupos(), getInstructores()]);
    } catch (error) {
      console.error("Error cargando datos base:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        "No se pudieron cargar los grupos e instructores.",
        "error"
      );
    } finally {
      setCargandoInicial(false);
    }
  }, [getGrupos, getInstructores, manejarErrorSesion]);

  const cargarDesdeLocalStorage = useCallback(() => {
    const asignacionStorage = localStorage.getItem("grupoInstructorEdit");

    if (!id || !asignacionStorage) return false;

    try {
      const asignacion = JSON.parse(asignacionStorage);

      setGrupoObjectId(asignacion.grupoObjectId || "");
      setInstructorObjectId(asignacion.instructorObjectId || "");

      setGrupoSeleccionado(
        asignacion.grupoObjectId
          ? {
              value: asignacion.grupoObjectId,
              label: asignacion.grupoLabel || "Grupo seleccionado",
            }
          : null
      );

      setInstructorSeleccionado(
        asignacion.instructorObjectId
          ? {
              value: asignacion.instructorObjectId,
              label:
                asignacion.instructorLabel || "Instructor seleccionado",
            }
          : null
      );

      setTipoAsignacion(asignacion.tipoAsignacion || "PRINCIPAL");
      setEstadoAsignacion(asignacion.estadoAsignacion || "ACTIVO");
      setObservacionAsignacion(asignacion.observacionAsignacion || "");

      return true;
    } catch (error) {
      console.error("Error leyendo grupoInstructorEdit:", error);
      localStorage.removeItem("grupoInstructorEdit");
      return false;
    }
  }, [id]);

  const getAsignacionById = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/grupoinstructores/listid/${id}`,
        authConfig()
      );

      if (!data?.ok || !data?.asignacion) {
        Swal.fire("Error", "No se encontró la asignación.", "error");
        return;
      }

      const asignacion = data.asignacion;
      const grupo = asignacion.grupoObjectId || {};
      const instructor = asignacion.instructorObjectId || {};

      const nombreInstructor = `${instructor.nombre1Instructor || ""} ${
        instructor.nombre2Instructor || ""
      } ${instructor.apellido1Instructor || ""} ${
        instructor.apellido2Instructor || ""
      } - ${instructor.documentoInstructor || "Sin documento"}`;

      setGrupoObjectId(grupo._id || "");
      setInstructorObjectId(instructor._id || "");

      setGrupoSeleccionado(
        grupo._id
          ? {
              value: grupo._id,
              label: `${grupo.fichaGrupo || "Sin ficha"}`,
            }
          : null
      );

      setInstructorSeleccionado(
        instructor._id
          ? {
              value: instructor._id,
              label: nombreInstructor,
            }
          : null
      );

      setTipoAsignacion(asignacion.tipoAsignacion || "PRINCIPAL");
      setEstadoAsignacion(asignacion.estadoAsignacion || "ACTIVO");
      setObservacionAsignacion(asignacion.observacionAsignacion || "");
    } catch (error) {
      console.error("Error cargando asignación:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message || "No fue posible cargar la asignación.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [id, authConfig, manejarErrorSesion]);

  useEffect(() => {
    cargarDatosBase();
  }, [cargarDatosBase]);

  useEffect(() => {
    if (isEdit) {
      const cargadoLocal = cargarDesdeLocalStorage();

      if (!cargadoLocal) {
        getAsignacionById();
      }
    }
  }, [isEdit, cargarDesdeLocalStorage, getAsignacionById]);

  const handleGrupoChange = (selectedOption) => {
    setGrupoSeleccionado(selectedOption || null);
    setGrupoObjectId(selectedOption?.value || "");
  };

  const handleInstructorChange = (selectedOption) => {
    setInstructorSeleccionado(selectedOption || null);
    setInstructorObjectId(selectedOption?.value || "");
  };

  const saveAsignacion = async () => {
    const nuevaAsignacion = {
      grupoObjectId,
      instructorObjectId,
      tipoAsignacion,
      estadoAsignacion,
      observacionAsignacion,
    };

    const { data } = await axios.post(
      "/api/grupoinstructores/add",
      nuevaAsignacion,
      authConfig()
    );

    if (!data?.ok) {
      Swal.fire(
        "Error",
        data?.message || "No se pudo registrar la asignación.",
        "error"
      );
      return;
    }

    await Swal.fire({
      icon: "success",
      title: "Asignación registrada con éxito",
      showConfirmButton: false,
      timer: 1500,
    });

    cleanData();
    navigate("/grupoinstructores");
  };

  const updateAsignacion = async () => {
    const asignacionUpdate = {
      grupoObjectId,
      instructorObjectId,
      tipoAsignacion,
      estadoAsignacion,
      observacionAsignacion,
    };

    const { data } = await axios.put(
      `/api/grupoinstructores/update/${id}`,
      asignacionUpdate,
      authConfig()
    );

    if (!data?.ok) {
      Swal.fire(
        "Error",
        data?.message || "No se pudo actualizar la asignación.",
        "error"
      );
      return;
    }

    await Swal.fire({
      icon: "success",
      title: "Asignación actualizada con éxito",
      showConfirmButton: false,
      timer: 1500,
    });

    cleanData();
    navigate("/grupoinstructores");
  };

  const actions = async (e) => {
    e.preventDefault();

    if (loading || cargandoInicial) return;

    if (!grupoObjectId) {
      Swal.fire("Atención", "Debe seleccionar un grupo.", "warning");
      return;
    }

    if (!instructorObjectId) {
      Swal.fire("Atención", "Debe seleccionar un instructor.", "warning");
      return;
    }

    try {
      setLoading(true);

      if (isEdit) {
        await updateAsignacion();
      } else {
        await saveAsignacion();
      }
    } catch (error) {
      console.error("Error guardando asignación:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message || "Hubo un problema con la solicitud.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    cleanData();
    navigate("/grupoinstructores");
  };

  const cargando = loading || cargandoInicial;

  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit
                    ? "Editar Asignación de Instructor"
                    : "Asignar Instructor a Grupo"}
                </h5>

                <small className="text-secondary">
                  Relacione un instructor con un grupo de formación.
                </small>
              </div>

              <div className="d-flex gap-2 flex-nowrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate("/grupoinstructores")}
                  disabled={cargando}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {cargando && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">
                  {cargandoInicial
                    ? "Cargando datos base…"
                    : isEdit
                    ? "Cargando o actualizando asignación…"
                    : "Procesando…"}
                </div>
              </div>
            )}

            <form
              id="form-grupo-instructores"
              onSubmit={actions}
              className="needs-validation"
              noValidate
            >
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">
                    Grupo / Ficha
                  </label>

                  <Select
                    options={opcionesGrupos}
                    value={grupoSeleccionado}
                    onChange={handleGrupoChange}
                    placeholder="Seleccione un grupo"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isDisabled={cargando}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">Instructor</label>

                  <Select
                    options={opcionesInstructores}
                    value={instructorSeleccionado}
                    onChange={handleInstructorChange}
                    placeholder="Seleccione un instructor"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isDisabled={cargando}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    Tipo de asignación
                  </label>

                  <select
                    className="form-select"
                    value={tipoAsignacion}
                    onChange={(e) => setTipoAsignacion(e.target.value)}
                    disabled={cargando}
                  >
                    <option value="PRINCIPAL">PRINCIPAL</option>
                    <option value="APOYO">APOYO</option>
                    <option value="SEGUIMIENTO">SEGUIMIENTO</option>
                  </select>
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">Estado</label>

                  <select
                    className="form-select"
                    value={estadoAsignacion}
                    onChange={(e) => setEstadoAsignacion(e.target.value)}
                    disabled={cargando}
                  >
                    <option value="ACTIVO">ACTIVO</option>
                    <option value="INACTIVO">INACTIVO</option>
                  </select>
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">Observación</label>

                  <input
                    type="text"
                    className="form-control"
                    placeholder="Observación opcional"
                    value={observacionAsignacion}
                    onChange={(e) => setObservacionAsignacion(e.target.value)}
                    disabled={cargando}
                  />
                </div>

                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-dark"
                      onClick={cleanData}
                      disabled={cargando}
                    >
                      <i className="fa-solid fa-eraser me-2"></i>
                      Limpiar
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={handleCancel}
                      disabled={cargando}
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      form="form-grupo-instructores"
                      className="btn btn-dark"
                      disabled={cargando}
                    >
                      <i
                        className={`fa-solid ${
                          cargando ? "fa-spinner fa-spin" : "fa-floppy-disk"
                        } me-2`}
                      ></i>

                      {cargando
                        ? isEdit
                          ? "Actualizando…"
                          : "Guardando…"
                        : isEdit
                        ? "Actualizar"
                        : "Guardar"}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarGrupoInstructores;